/**
 * Conflict Detection Panel Component
 * Displays conflict warnings and resolution options
 */

import React, { useState } from 'react';
import { Card, Button, Badge } from '@tracekit/core';
import {
  AlertTriangle,
  XCircle,
  CheckCircle,
  Info,
  Search,
  Clock,
  Users,
  Award,
  ArrowRight,
} from 'lucide-react';

interface Conflict {
  staff_member_id: string;
  staff_name: string;
  conflict_type: 'over_allocation' | 'schedule_conflict' | 'capacity_exceeded' | 'designation_gap';
  severity: 'high' | 'medium' | 'low';
  message: string;
  utilization_percent?: number;
  missing_designations?: string[];
}

interface ConflictData {
  conflicts: Conflict[];
  warnings: Array<{
    staff_member_id: string;
    staff_name: string;
    warning_type: string;
    severity: string;
    message: string;
    utilization_percent: number;
  }>;
  designation_gaps: Conflict[];
  total_conflicts: number;
  total_warnings: number;
  total_designation_gaps: number;
}

interface ConflictDetectionPanelProps {
  conflictData: ConflictData | null;
  isLoading?: boolean;
  onFindAlternatives?: (staffId: string, requirementId?: string) => void;
  onAdjustTimeline?: (staffId: string) => void;
  onRequestStaff?: () => void;
}

export function ConflictDetectionPanel({
  conflictData,
  isLoading = false,
  onFindAlternatives,
  onAdjustTimeline,
  onRequestStaff,
}: ConflictDetectionPanelProps) {
  const [expandedConflicts, setExpandedConflicts] = useState<Set<string>>(new Set());

  const toggleConflict = (staffId: string) => {
    const newExpanded = new Set(expandedConflicts);
    if (newExpanded.has(staffId)) {
      newExpanded.delete(staffId);
    } else {
      newExpanded.add(staffId);
    }
    setExpandedConflicts(newExpanded);
  };

  const getConflictIcon = (type: string) => {
    switch (type) {
      case 'over_allocation':
      case 'capacity_exceeded':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'schedule_conflict':
        return <Clock className="w-5 h-5 text-orange-600" />;
      case 'designation_gap':
        return <Award className="w-5 h-5 text-purple-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getConflictColor = (type: string, severity: string) => {
    if (severity === 'high') {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    } else if (severity === 'medium') {
      return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    }
    return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
  };

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Detecting conflicts...</span>
        </div>
      </Card>
    );
  }

  if (!conflictData || (conflictData.total_conflicts === 0 && conflictData.total_warnings === 0 && conflictData.total_designation_gaps === 0)) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-2">
            No Conflicts Detected
          </h3>
          <p className="text-gray-600 dark:text-theme-text-secondary">
            All staff members are within capacity limits and have no scheduling conflicts.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card className="p-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{conflictData.total_conflicts}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Conflicts</div>
          </div>
          <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{conflictData.total_warnings}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Warnings</div>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{conflictData.total_designation_gaps}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Designation Gaps</div>
          </div>
        </div>
      </Card>

      {/* Conflicts */}
      {conflictData.conflicts.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            Critical Conflicts ({conflictData.conflicts.length})
          </h3>
          <div className="space-y-3">
            {conflictData.conflicts.map((conflict, idx) => {
              const isExpanded = expandedConflicts.has(conflict.staff_member_id);
              
              return (
                <Card
                  key={idx}
                  className={`p-4 border-2 ${getConflictColor(conflict.conflict_type, conflict.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getConflictIcon(conflict.conflict_type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 dark:text-theme-text">
                            {conflict.staff_name}
                          </span>
                          <Badge
                            variant={conflict.severity === 'high' ? 'error' : 'warning'}
                            className="text-xs"
                          >
                            {conflict.severity}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {conflict.conflict_type.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {conflict.message}
                        </p>
                        {conflict.utilization_percent !== undefined && (
                          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                            Utilization: {conflict.utilization_percent.toFixed(1)}%
                          </div>
                        )}
                        {conflict.missing_designations && conflict.missing_designations.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Missing Designations:
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {conflict.missing_designations.map((desig, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {desig}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleConflict(conflict.staff_member_id)}
                    >
                      {isExpanded ? 'Hide' : 'Show'} Options
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="font-medium mb-3 text-gray-900 dark:text-theme-text">
                        Resolution Options:
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => onFindAlternatives?.(conflict.staff_member_id)}
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Find Alternatives
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => onAdjustTimeline?.(conflict.staff_member_id)}
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Adjust Timeline
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => onRequestStaff?.()}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Request Staff
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Warnings */}
      {conflictData.warnings.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            Capacity Warnings ({conflictData.warnings.length})
          </h3>
          <div className="space-y-2">
            {conflictData.warnings.map((warning, idx) => (
              <Card
                key={idx}
                className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-theme-text">
                      {warning.staff_name}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                      - {warning.message}
                    </span>
                  </div>
                  <Badge variant="warning" className="text-xs">
                    {warning.utilization_percent.toFixed(1)}%
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Designation Gaps */}
      {conflictData.designation_gaps.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-600" />
            Designation Gaps ({conflictData.designation_gaps.length})
          </h3>
          <div className="space-y-2">
            {conflictData.designation_gaps.map((gap, idx) => (
              <Card
                key={idx}
                className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-theme-text mb-1">
                      {gap.staff_name}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {gap.message}
                    </p>
                    {gap.missing_designations && gap.missing_designations.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {gap.missing_designations.map((desig, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {desig}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onFindAlternatives?.(gap.staff_member_id)}
                    className="ml-3"
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Find Alternative
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Conflict Resolution:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>Find Alternatives:</strong> Search for staff with similar qualifications</li>
              <li><strong>Adjust Timeline:</strong> Review and negotiate deadlines, stagger assignments</li>
              <li><strong>Request Staff:</strong> Create a staff request for additional resources</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
